import React, { useEffect, useCallback, useState, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { devError } from "@/lib/logger";

// Pages
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminEmployees from "@/pages/admin/Employees";
import AdminSchedules from "@/pages/admin/Schedules";
import AdminReports from "@/pages/admin/Reports";
import AdminRequests from "@/pages/admin/Requests";
import AdminSettings from "@/pages/admin/Settings";
import AdminLocations from "@/pages/admin/Locations";
import AdminPhotos from "@/pages/admin/Photos";
import AdminKpis from "@/pages/admin/KPIs";
import AdminAdjustments from "@/pages/admin/Adjustments";
import Kiosk from "@/pages/Kiosk";
import PortalLogin from "@/pages/portal/Login";
import PortalDashboard from "@/pages/portal/Dashboard";
import PortalRequests from "@/pages/portal/Requests";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Usa XMLHttpRequest para evitar problemas de extensões/service workers que
    // bloqueiam a leitura do body de fetch ("body is disturbed or locked")
    const doMeRequest = () => new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API}/auth/me`, true);
        xhr.withCredentials = true;
        xhr.timeout = 10000;
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText || '{}')); }
            catch { resolve(null); }
          } else {
            resolve(null);
          }
        };
        xhr.onerror = () => reject(new Error('network'));
        xhr.ontimeout = () => reject(new Error('timeout'));
        xhr.send();
      } catch (err) { reject(err); }
    });

    try {
      const userData = await doMeRequest();
      if (userData && userData.user_id) setUser(userData);
    } catch (err) {
      devError('Auth check', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      devError('Logout', err);
    }
    setUser(null);
  }, []);

  const authValue = useMemo(() => ({ user, setUser, loading, logout, checkAuth }), [user, loading, logout, checkAuth]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground uppercase tracking-widest">Carregando...</p>
        </div>
      </div>
    );
  }

  // If user data passed from AuthCallback, allow access
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (synchronous - before first render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/employees" element={<ProtectedRoute><AdminEmployees /></ProtectedRoute>} />
      <Route path="/admin/schedules" element={<ProtectedRoute><AdminSchedules /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/requests" element={<ProtectedRoute><AdminRequests /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/locations" element={<ProtectedRoute><AdminLocations /></ProtectedRoute>} />
      <Route path="/admin/photos" element={<ProtectedRoute><AdminPhotos /></ProtectedRoute>} />
      <Route path="/admin/kpis" element={<ProtectedRoute><AdminKpis /></ProtectedRoute>} />
      <Route path="/admin/adjustments" element={<ProtectedRoute><AdminAdjustments /></ProtectedRoute>} />
      
      {/* Kiosk Route */}
      <Route path="/kiosk/:locationId" element={<Kiosk />} />
      <Route path="/kiosk" element={<Kiosk />} />
      
      {/* Portal Routes */}
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />
      <Route path="/portal/requests" element={<PortalRequests />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-container">
          <AppRouter />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
